/* eslint-disable no-useless-call */
import merge from 'lodash/merge'
import mergeWith from 'lodash/mergeWith'
import { v4 as uuidv4 } from 'uuid'
import uuid62 from 'uuid62'

/**
 * get singleton
 * @param t new creation type
 */
export const singlton = <T>(t: { new (): T }): T => {
  const global = getGlobalObject()
  if (!global) {
    // eslint-disable-next-line new-cap
    return new t()
  }

  if (!global[t.name]) {
    // eslint-disable-next-line new-cap
    global[t.name] = new t()
  }

  return global[t.name] as T
}

/**
 * deepcopy object
 * @param input source object
 * @param rootOverwrites merge object
 */
// export const deepCopy = <T extends {}>(input: any, rootOverwrites?: any): T => {
//   return JSON.parse(JSON.stringify({ ...input, ...rootOverwrites }))
// }

/**
 * assign objects
 * @param args object to assign
 */
export const assign = <T>(...args: T[] | Partial<T>[]): T => {
  // @ts-ignore
  return Object.assign.apply(Object, [{}, ...args])
}

/**
 * deepmerge objects
 * @param object {<T>} object
 * @param source {<T>} source
 */
export const deepMerge = <T>(object: T, ...sources: T[]): T => {
  let tempObj = object

  sources.forEach(source => {
    tempObj = merge(object, source)
  })

  return tempObj
}

/**
 * deepmerge objects (when object value is ignore null value object)
 * @param object object
 * @param sources target sources
 */
export const deepMergeWithNull = <T>(object: T, ...sources: T[]): T => {
  let tempObj = object

  sources.forEach(source => {
    tempObj = mergeWith(object, source, (a, b) => (b === null ? a : undefined))
  })

  return tempObj
}

/**
 * object의 function을 제거한다.
 * @param object
 */
export const removeFunction = (obj: object, depth: number = 1): object => {
  if (!obj || depth > 20) {
    return obj
  }
  Object.keys(obj).forEach(k => {
    if (typeof obj[k] === 'function') {
      delete obj[k]
    } else if (Array.isArray(obj[k])) {
      obj[k] = obj[k].filter(item => typeof item !== 'function')

      obj[k].forEach(v => {
        if (typeof v === 'object') {
          removeFunction(v, ++depth)
        }
      })
    } else if (typeof obj[k] === 'object') {
      removeFunction(obj[k], ++depth)
    }
  })

  return obj
}

/**
 * create uuid
 * @param id {string} 생성할 id 기준 문자열 없을 경우, random
 */
export const uuid = (id?: string) => {
  return uuidv4(id)
}

/**
 * create base62 converted uuid
 */
export const uuidBase62 = () => {
  return uuid62.v4()
}

/**
 * call stack 조회
 * @exports
 * @returns {string}
 */
export const getStack = function (): string {
  return new Error().stack
}

/**
 * examines the call stack and returns a string indicating
 * the file and line number of the n'th previous ancestor call.
 * this works in chrome, and should work in nodejs as well.
 *
 * @param n : int (default: n=1) - the number of calls to trace up the
 *   stack from the current call.  `n=0` gives you your current file/line.
 *  `n=1` gives the file/line that called you.
 */
export const traceCaller = function (n: number) {
  if (isNaN(n) || n < 0) n = 1
  n += 1
  let s = new Error().stack

  if (!s) {
    return ' '
  }

  let a = s.indexOf('\n', 5)
  while (n--) {
    a = s.indexOf('\n', a + 1)
    if (a < 0) {
      a = s.lastIndexOf('\n', s.length)
      break
    }
  }
  let b = s.indexOf('\n', a + 1)
  if (b < 0) b = s.length
  a = Math.max(s.lastIndexOf(' ', b), s.lastIndexOf('/', b))
  b = s.lastIndexOf(':', b)
  s = s.substring(a + 1, b)
  return s
}

/**
 * 유효한 argument 조회
 */
export const getArgs = (): string[] => {
  return process.argv.slice(2)
}

/**
 * 해당 argument가 포함되어있는지 확인
 * @param arg
 */
export const isIncludeArgs = (arg: string): boolean => {
  return getArgs().filter(a => a === arg).length > 0
}

/**
 * require funciton for esm
 * @param option
 */
// export const requireForEsm = (option?: any) => {
//   return require('esm')(module, option)
// }

/**
 * 특정 value에 해당하는 object key 반환
 * @param enumObj object
 * @param enumVal val
 */
export const getObjectKey = (obj: object, _val: any): string => {
  if (!obj) {
    return null
  }

  for (const enumKey of Object.keys(obj)) {
    if (obj[enumKey] === obj) {
      return enumKey as string
    }
  }

  return null
}

/**
 * object에 property 반영
 * @param obj opbject
 * @param propName property명
 * @param value property value
 */
export const setProperty = (obj: object | Function, propName: string, value: any): object => {
  if (!obj) {
    return obj
  }

  obj[propName] = value
}

/**
 * object propety 조회
 * @param obj object
 * @param propName property 명
 */
export const getProperty = <T = any>(obj: object | Function, propName: string): T => {
  if (!obj) {
    return null
  }
  return obj[propName] as T
}

/**
 * property를 제거한다.
 * @param _obj 대상 object
 * @param propPath 프로퍼티 경로 (ex:app.server)
 */
export const deleteProperty = (_obj: object | Function, propPath: string) => {
  let objectStr = 'delete _obj'

  for (const propName of propPath.split('.')) {
    objectStr += `['${propName}']`
  }

  try {
    // eslint-disable-next-line no-eval
    eval(objectStr)
  } catch (e) {}
}

/**
 * 전역 저장 객체 조회
 */
export const getGlobalObject = () => {
  if (typeof window !== 'undefined') {
    return window
  } else {
    return global
  }
}

/**
 * 지정된 ms 기준 지연 처리
 * @param ms
 */
export const sleep = (ms: number = 0): Promise<void> => {
  return new Promise(resolve => {
    setTimeout(resolve, ms)
  })
}

/**
 * 지정된 필드 명 제외 후 리턴
 * @param obj
 * @param fieldNames
 */
export const exclude = (obj: Object, ...fieldNames) => {
  const newObj = {}

  for (const key in obj) {
    if (fieldNames.includes(key)) {
      newObj[key] = obj[key]
    }
  }

  return newObj
}

/**
 * value 값이 null 또는 undefined일 경우, default 값 반환
 * @param value 대상 값
 * @param defaultValue 기본 값
 */
export const defaults = <T>(value: T, defaultValue: T): T => {
  if (typeof value === 'undefined' || value === null) {
    return defaultValue
  }

  return value
}

/**
 * 객체를 비동기로 조회
 *  비동기로 로드되는 객체가 인식될 때까지 대기하여 객체를 조회
 * @param objectFn 객체 조회 function
 * @param timeout  최대 대기 ms (0일 경우 무제한) @default 0
 */
export const getObjectAsync = <T>(objectFn: () => T, timeout = 0): Promise<T> => {
  if (!objectFn) {
    return Promise.resolve(null)
  }

  return new Promise(resolve => {
    let timer = getProperty(objectFn, 'timer')
    if (timeout > 0 && !timer) {
      timer = setTimeout(() => {
        const resolved = getProperty(objectFn, 'resolved')

        if (resolved) {
          return
        }
        setProperty(objectFn, 'resolved', true)
        deleteProperty(objectFn, 'timer')
        resolve(null)
      }, timeout)
    }

    const getObject = () => {
      const obj = objectFn()

      if (obj) {
        const resolved = getProperty(objectFn, 'resolved')

        if (resolved) {
          return
        }

        setProperty(objectFn, 'resolved', true)

        const timer = getProperty(objectFn, 'timer')
        if (timer) {
          clearTimeout(timer)
        }

        deleteProperty(objectFn, 'timer')
        resolve(obj)
      } else {
        setTimeout(() => getObject(), 50)
      }
    }

    getObject()
  })
}

/**
 * singleton 객체를 리턴하기 위한 super class
 * @export
 * @class SingletonObject
 */
export class SingletonObject {
  /**
   * singleton 객체 리턴
   * @param t new creation type
   * @see {CoreUtils}
   */
  static singleton<T>(t: { new (): T }): T {
    return singlton(t)
  }
}
